import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PriceCheckRoundedIcon from "@mui/icons-material/PriceCheckRounded";
import {
  getAdditionalActReadiness,
  isValidAdditionalActAmount,
} from "./additionalActReadiness";
import {
  getAdditionalActWorkflow,
  isConsignmentAdditionalAct,
} from "./additionalActWorkflow";

const priceInputStyles = {
  "& .MuiOutlinedInput-root": {
    color: "#F8FAFC",
    borderRadius: 3,
    backgroundColor: "rgba(15, 23, 42, 0.48)",
    "& fieldset": { borderColor: "rgba(148, 163, 184, 0.32)" },
    "&:hover fieldset": { borderColor: "rgba(250, 204, 21, 0.7)" },
    "&.Mui-focused fieldset": { borderColor: "#FACC15" },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(226, 232, 240, 0.74)",
    "&.Mui-focused": { color: "#FDE047" },
  },
};

const RenegocierePanel = ({
  currentPrice,
  newPrice,
  acceptedPrice,
  fallbackPrice,
  onChangeNewPrice,
  onSaveNewPrice,
  isAcceptingPrice = false,
  onRejectPrice,
  jiraFields,
  vendorData,
  onCreateActAditional,
  isCreatingAct = false,
  creationFeedback,
  additionalActPreview,
}) => {
  const readiness = getAdditionalActReadiness({
    jiraFields,
    vendorData,
    currentPrice,
    newPrice,
    acceptedPrice,
    fallbackPrice,
    additionalActPreview,
  });
  const isConsignment = isConsignmentAdditionalAct(jiraFields?.Contract);
  const isZoho = getAdditionalActWorkflow(jiraFields?.Contract).provider === "zoho";
  const progress = readiness.totalCount
    ? (readiness.completedCount / readiness.totalCount) * 100
    : 0;
  const hasProposedPrice = Boolean(String(newPrice || "").trim());
  const hasValidProposedPrice = isValidAdditionalActAmount(newPrice);

  return (
    <Stack spacing={3}>
      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "#FDE047", fontWeight: 800, letterSpacing: 0.3 }}
            >
              Renegociere
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: "rgba(226, 232, 240, 0.72)" }}
            >
              Confirmă noua sumă, apoi verifică datele necesare actului
              adițional.
            </Typography>
          </Box>
          <Chip
            icon={
              readiness.isPriceAccepted ? (
                <CheckCircleRoundedIcon />
              ) : (
                <PriceCheckRoundedIcon />
              )
            }
            label={
              readiness.isPriceAccepted
                ? "Suma nouă este acceptată"
                : "Suma nouă trebuie acceptată"
            }
            sx={{
              flexShrink: 0,
              color: readiness.isPriceAccepted ? "#BBF7D0" : "#FEF08A",
              bgcolor: readiness.isPriceAccepted
                ? "rgba(34, 197, 94, 0.14)"
                : "rgba(250, 204, 21, 0.12)",
              border: `1px solid ${
                readiness.isPriceAccepted
                  ? "rgba(74, 222, 128, 0.35)"
                  : "rgba(250, 204, 21, 0.32)"
              }`,
              "& .MuiChip-icon": {
                color: readiness.isPriceAccepted ? "#4ADE80" : "#FACC15",
              },
            }}
          />
        </Stack>

        <Box
          sx={{
            mt: 2.5,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          <TextField
            label="Preț actual"
            value={currentPrice || ""}
            variant="outlined"
            fullWidth
            InputProps={{ readOnly: true }}
            helperText=" "
            sx={priceInputStyles}
          />
          <TextField
            label="Preț propus"
            value={newPrice || ""}
            onChange={(event) => onChangeNewPrice(event.target.value)}
            variant="outlined"
            fullWidth
            inputProps={{ inputMode: "decimal" }}
            error={hasProposedPrice && !hasValidProposedPrice}
            helperText={
              hasProposedPrice && !hasValidProposedPrice
                ? "Introdu o sumă mai mare decât zero."
                : " "
            }
            sx={priceInputStyles}
          />
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="center"
          sx={{ mt: 2 }}
        >
          <Button
            variant="contained"
            onClick={onSaveNewPrice}
            disabled={
              !hasValidProposedPrice ||
              readiness.isPriceAccepted ||
              isAcceptingPrice
            }
            startIcon={
              isAcceptingPrice ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CheckCircleRoundedIcon />
              )
            }
            sx={{
              minWidth: 180,
              py: 1.15,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: "#16A34A",
              boxShadow: "0 10px 24px rgba(22, 163, 74, 0.24)",
              "&:hover": { bgcolor: "#15803D" },
            }}
          >
            {isAcceptingPrice
              ? "Se salvează..."
              : readiness.isPriceAccepted
                ? "Preț acceptat"
                : "Acceptă prețul"}
          </Button>
          <Button
            variant="outlined"
            onClick={onRejectPrice}
            startIcon={<CancelRoundedIcon />}
            sx={{
              minWidth: 180,
              py: 1.15,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 800,
              color: "#FDA4AF",
              borderColor: "rgba(251, 113, 133, 0.5)",
              "&:hover": {
                borderColor: "#FB7185",
                bgcolor: "rgba(244, 63, 94, 0.1)",
              },
            }}
          >
            Refuză prețul
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 4,
          bgcolor: "rgba(15, 23, 42, 0.5)",
          border: `1px solid ${
            readiness.isReady
              ? "rgba(74, 222, 128, 0.34)"
              : "rgba(248, 113, 113, 0.24)"
          }`,
          boxShadow: readiness.isReady
            ? "0 16px 42px rgba(22, 163, 74, 0.12)"
            : "0 16px 42px rgba(15, 23, 42, 0.22)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box>
            <Typography sx={{ color: "#F8FAFC", fontWeight: 800 }}>
              {isConsignment
                ? "LP013 — Act adițional de consignație · Zoho"
                : isZoho
                  ? "LP014 — Act adițional de vânzare directă · Zoho"
                  : "Act adițional"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.35, color: "rgba(203, 213, 225, 0.72)" }}
            >
              {readiness.isReady
                ? "Toate informațiile sunt disponibile. Documentul poate fi creat."
                : `Completează ${readiness.missingCount} ${
                    readiness.missingCount === 1
                      ? "informație marcată cu roșu"
                      : "informații marcate cu roșu"
                  }.`}
            </Typography>
          </Box>
          <Chip
            label={`${readiness.completedCount}/${readiness.totalCount} completate`}
            sx={{
              flexShrink: 0,
              fontWeight: 800,
              color: readiness.isReady ? "#BBF7D0" : "#FECACA",
              bgcolor: readiness.isReady
                ? "rgba(34, 197, 94, 0.15)"
                : "rgba(239, 68, 68, 0.12)",
            }}
          />
        </Stack>

        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label={`${readiness.completedCount} din ${readiness.totalCount} condiții completate`}
          sx={{
            mt: 2,
            height: 7,
            borderRadius: 999,
            bgcolor: "rgba(148, 163, 184, 0.14)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              bgcolor: readiness.isReady ? "#22C55E" : "#F59E0B",
            },
          }}
        />

        {isZoho && additionalActPreview?.loading && (
          <Alert
            severity="info"
            icon={<CircularProgress size={20} color="inherit" />}
            sx={{ mt: 2, borderRadius: 2.5 }}
          >
            Se verifică prețul inițial și raportul Quality Check din JIRA…
          </Alert>
        )}
        {isZoho && additionalActPreview?.error && (
          <Alert
            severity="error"
            sx={{ mt: 2, borderRadius: 2.5 }}
            action={
              <Button color="inherit" onClick={additionalActPreview.refresh}>
                Reîncearcă
              </Button>
            }
          >
            {additionalActPreview.error}
          </Alert>
        )}
        {isZoho && additionalActPreview?.data?.qcReport?.filename && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 2, color: "rgba(203, 213, 225, 0.72)" }}
          >
            Raport QC: {additionalActPreview.data.qcReport.filename}
          </Typography>
        )}

        <Box
          component="ul"
          sx={{
            p: 0,
            m: 0,
            mt: 2,
            listStyle: "none",
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
            },
            gap: 1.25,
          }}
        >
          {readiness.requirements.map((item) => (
            <Box
              component="li"
              key={item.id}
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: item.isComplete
                  ? "rgba(34, 197, 94, 0.08)"
                  : "rgba(239, 68, 68, 0.08)",
                border: `1px solid ${
                  item.isComplete
                    ? "rgba(74, 222, 128, 0.22)"
                    : "rgba(248, 113, 113, 0.22)"
                }`,
              }}
            >
              {item.isComplete ? (
                <CheckCircleRoundedIcon
                  aria-hidden="true"
                  sx={{ mt: 0.1, color: "#4ADE80", fontSize: 22 }}
                />
              ) : (
                <CancelRoundedIcon
                  aria-hidden="true"
                  sx={{ mt: 0.1, color: "#F87171", fontSize: 22 }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#F1F5F9", fontWeight: 750, lineHeight: 1.35 }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.35,
                    color: item.isComplete
                      ? "rgba(187, 247, 208, 0.78)"
                      : "#FCA5A5",
                    lineHeight: 1.4,
                    overflowWrap: "anywhere",
                  }}
                >
                  {item.isComplete ? item.value : item.missingMessage}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {creationFeedback?.message && (
          <Alert
            severity={creationFeedback.severity || "info"}
            sx={{ mt: 2, borderRadius: 2.5 }}
          >
            {creationFeedback.message}
          </Alert>
        )}

        <Box
          sx={{
            mt: 2.5,
            pt: 2.5,
            borderTop: "1px solid rgba(148, 163, 184, 0.16)",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: readiness.isReady
                ? "rgba(187, 247, 208, 0.82)"
                : "rgba(254, 202, 202, 0.82)",
            }}
          >
            {creationFeedback?.preventRetry
              ? "Trimiterea este blocată. Vezi mesajul de mai sus."
              : readiness.isReady
              ? "Pregătit pentru creare și trimitere la semnat."
              : "Butonul devine activ după completarea tuturor condițiilor."}
          </Typography>
          <Button
            variant="contained"
            onClick={onCreateActAditional}
            disabled={!readiness.isReady || isCreatingAct || creationFeedback?.preventRetry}
            startIcon={
              isCreatingAct ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DescriptionOutlinedIcon />
              )
            }
            sx={{
              minWidth: { sm: 260 },
              py: 1.2,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: "#2563EB",
              boxShadow: "0 12px 28px rgba(37, 99, 235, 0.28)",
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            {isCreatingAct
              ? "Se creează actul..."
              : isZoho
                ? "Creează și trimite prin Zoho"
                : "Creează și trimite actul adițional"}
          </Button>
        </Box>
      </Box>
    </Stack>
  );
};

export default RenegocierePanel;
